"use client";
import { ModuleIcon } from "./ModuleIcon";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { EDGE_BBOX, EdgePaths } from "./EdgePaths";
import anim from "./EdgeHubStage.module.css";

/**
 * Home hero illustration: a compact PSCX Edge chat with the company's systems plugged in
 * on the left through one aggregation rail, a self-running chat in the center,
 * and example outputs on the right. The current workflow result appears below.
 *
 * One task animates at a time. On first load the tiles report Connected.
 * Then, per scenario: the request types in → Edge thinks → the
 * systems involved illuminate with a short activity label ("Reading directory")
 * while Edge states what it is checking → Edge answers with a
 * finding or a proposed change → changes wait at "Awaiting approval" until the visitor
 * clicks Approve example (or a few seconds pass; either way it is labelled simulated) →
 * the affected systems report activity while the change applies → the result appears
 * and supported output examples are checked after verification → hold → next scenario.
 *
 * This is an illustrative workflow; category icons only, no vendor marks, no
 * metrics. Reduced motion renders the completed scene, no timers. Animation pauses when
 * the illustration is offscreen. Dimensions are fixed so no
 * phase shifts the page.
 */

type SourceId = "ucaas" | "contact-center" | "directories" | "workflows" | "devices";

const SOURCES: { id: SourceId; label: string }[] = [
  { id: "ucaas", label: "UCaaS" },
  { id: "contact-center", label: "Contact Center" },
  { id: "directories", label: "Databases, CRM & ERP" },
  { id: "workflows", label: "Workflows" },
  { id: "devices", label: "Devices" },
];
const source = (id: SourceId) => SOURCES.find((s) => s.id === id)!;

type Phase = "connecting" | "typing" | "thinking" | "gathering" | "response" | "approval" | "executing" | "result" | "verified";

const CHANGE_PHASES: Phase[] = ["typing", "thinking", "gathering", "response", "approval", "executing", "result", "verified"];
const READ_PHASES: Phase[] = ["typing", "thinking", "gathering", "response", "result", "verified"];

/** Milliseconds each phase holds before advancing. Typing is paced per character. */
const HOLD: Record<Exclude<Phase, "typing">, number> = {
  connecting: 2200,
  thinking: 900,
  gathering: 2600,
  response: 4800, // change scenarios: how long we wait for a click before simulating approval
  approval: 1100,
  executing: 2200,
  result: 1300, // the pause when a result arrives, before verification
  verified: 4600,
};
const TYPE_MS = 36;

type ResultView =
  | { kind: "checklist"; title: string; items: string[] }
  | { kind: "findings"; title: string; items: string[] }
  | { kind: "dashboard"; title: string; charts: { label: string; bars: number[] }[] }
  | { kind: "fix"; title: string; body: string };

interface Scenario {
  id: "employee" | "licenses" | "dashboard" | "wolf";
  label: string;
  request: string;
  /** Who answers: Edge, or The Wolf for troubleshooting. */
  agent: "edge" | "wolf";
  /** Systems read while gathering, with the activity label shown beside each. */
  reads: Partial<Record<SourceId, string>>;
  /** Systems changed while executing, with the activity label. Empty for read-only work. */
  writes: Partial<Record<SourceId, string>>;
  /** What Edge says it is checking, shown while gathering. */
  finding: string;
  /** Edge's answer: a finding, or a proposed change awaiting approval. */
  response: string;
  running: string;
  result: ResultView;
}

const SCENARIOS: Scenario[] = [
  {
    id: "employee",
    label: "Employee setup",
    request: "Set up our new employee.",
    agent: "edge",
    reads: { directories: "Reading directory", ucaas: "Checking licenses", "contact-center": "Checking queues", devices: "Finding a phone" },
    writes: { ucaas: "Applying setup", "contact-center": "Applying setup", directories: "Applying setup", devices: "Applying setup" },
    finding: "Checking role, licenses, calling policy, and device availability.",
    response: "Voice, queue access, directory entry, and a desk phone are ready to provision.",
    running: "Provisioning across four systems…",
    result: { kind: "checklist", title: "Employee provisioned", items: ["UCaaS user created", "Queue assigned", "Directory synced", "Phone provisioned"] },
  },
  {
    id: "licenses",
    label: "License review",
    request: "Find unused licenses.",
    agent: "edge",
    reads: { ucaas: "Checking licenses", "contact-center": "Checking seats", directories: "Reading directory" },
    writes: {},
    finding: "Comparing licenses with recent activity and duplicate entitlements.",
    response: "License review ready. Nothing changes until you ask.",
    running: "",
    result: { kind: "findings", title: "License review ready", items: ["Licenses idle 90+ days", "Duplicate entitlements", "Over-licensed users"] },
  },
  {
    id: "dashboard",
    label: "Dashboard",
    request: "Show my environment dashboard.",
    agent: "edge",
    reads: { ucaas: "Reading usage", "contact-center": "Reading queues", directories: "Reading directory", workflows: "Reading runs", devices: "Reading health" },
    writes: {},
    finding: "Summarizing utilization, compliance, and device health.",
    response: "Here’s your environment dashboard.",
    running: "",
    result: {
      kind: "dashboard",
      title: "Environment dashboard",
      charts: [
        { label: "Utilization", bars: [0.5, 0.62, 0.58, 0.7, 0.76, 0.82] },
        { label: "Compliance", bars: [0.7, 0.72, 0.8, 0.86, 0.9, 0.96] },
        { label: "Device health", bars: [0.9, 0.88, 0.94, 0.92, 0.97, 0.98] },
      ],
    },
  },
  {
    id: "wolf",
    label: "Wolf troubleshooting",
    request: "Calls to the support line are dropping.",
    agent: "wolf",
    reads: { "contact-center": "Checking routing", ucaas: "Checking policy", devices: "Checking firmware", workflows: "Recent changes" },
    writes: { "contact-center": "Applying fix" },
    finding: "Checking support routing and changes from the last 24 hours.",
    response: "A recent change points overflow calls to a disabled queue. Restore the standard target?",
    running: "Restoring overflow routing…",
    result: { kind: "fix", title: "Routing restored", body: "Overflow target reset to the standard queue; a test call completed." },
  },
];

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

export function EdgeHubStage() {
  const reduced = useReducedMotion();
  const root = useRef<HTMLElement>(null);
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("connecting");
  const [typed, setTyped] = useState("");
  const [visible, setVisible] = useState(true);
  const [approvedByClick, setApprovedByClick] = useState(false);

  const scenario = SCENARIOS[scenarioIndex] ?? SCENARIOS[0]!;
  const isChange = Object.keys(scenario.writes).length > 0;
  const phases = isChange ? CHANGE_PHASES : READ_PHASES;
  const running = visible && !reduced;

  // Reduced motion: the finished scene, no timers.
  useEffect(() => {
    if (!reduced) return;
    // This switches to the static, completed scene when the preference changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPhase("verified");
    setTyped(scenario.request);
  }, [reduced, scenario]);

  // Pause when offscreen.
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setVisible(!!entry?.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Keep updater functions pure: React may evaluate them twice in development.
  const advance = useCallback(() => {
    if (phase === "connecting") {
      setPhase("typing");
      return;
    }
    const next = phases[phases.indexOf(phase) + 1];
    if (next) {
      setPhase(next);
      return;
    }
    setScenarioIndex(i => (i + 1) % SCENARIOS.length);
    setTyped("");
    setApprovedByClick(false);
    setPhase("typing");
  }, [phase, phases]);

  // Typing is paced per character; every other phase holds for a fixed time.
  useEffect(() => {
    if (!running) return;
    if (phase === "typing") {
      if (typed.length >= scenario.request.length) {
        const t = setTimeout(() => setPhase("thinking"), 350);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setTyped(scenario.request.slice(0, typed.length + 1)), TYPE_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(advance, HOLD[phase]);
    return () => clearTimeout(t);
  }, [running, phase, typed, scenario, advance]);

  const approve = useCallback(() => {
    if (phase !== "response" || !isChange) return;
    setApprovedByClick(true);
    setPhase("approval");
  }, [phase, isChange]);

  const idx = (p: Phase) => phases.indexOf(p);
  const at = (p: Phase) => phase !== "connecting" && idx(phase) >= idx(p);

  // Which systems are lit, and what each is doing right now.
  const activity: Partial<Record<SourceId, string>> =
    phase === "connecting" ? Object.fromEntries(SOURCES.map((s) => [s.id, "Connecting…"])) : phase === "gathering" ? scenario.reads : phase === "executing" ? scenario.writes : {};
  const lit = new Set<SourceId>(
    phase === "gathering" || phase === "response" || phase === "approval" ? (Object.keys(scenario.reads) as SourceId[]) : phase === "executing" || phase === "result" || phase === "verified" ? (Object.keys(isChange ? scenario.writes : scenario.reads) as SourceId[]) : [],
  );

  const description = `Automated demonstration. UCaaS, Contact Center, Databases, CRM and ERP, Workflows, and Devices connect into a PSCX Edge chat. The visitor asks “${scenario.request}” ${scenario.agent === "wolf" ? "The Wolf" : "Edge"} checks the connected systems and answers “${scenario.response}”${isChange ? " then waits for approval before making the change" : ""}; the result “${scenario.result.title}” is verified.`;

  const hub = <ChatHub scenario={scenario} phase={phase} typed={typed} at={at} onApprove={approve} approvedByClick={approvedByClick} interactive={running} />;
  const result = (
    <section aria-label="Resolution" data-resolution>
      <header className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="font-display text-base font-semibold text-ink">Resolution</h3>
        <p className="font-sans text-xs text-slate">Verified across connected systems</p>
      </header>
      <ResultSlot scenario={scenario} phase={phase} at={at} />
    </section>
  );
  const outputs = <OutputCatalog scenario={scenario} verified={phase === "verified"} />;
  const eyebrow = "mb-4 font-sans text-[10px] font-semibold uppercase tracking-[0.13em] text-signal-ink";

  return (
    <figure ref={root} data-workflow={scenario.id} data-phase={phase} aria-label={description} className={`${anim.stageIn} mx-auto w-full max-w-[45rem]`}>
      <div className="relative hidden grid-cols-[9.375rem_20rem_9.375rem] items-start justify-center gap-x-7 min-[1180px]:grid" data-desktop-stage>
        <Connectors scopeRef={root} />
        <div>
          <h2 className={eyebrow}>Connected inputs</h2>
          <ul className="flex flex-col gap-4">
            {SOURCES.map((s) => (
              <li key={s.id} data-tile={s.id}><SourceTile id={s.id} lit={lit.has(s.id)} activity={activity[s.id]} /></li>
            ))}
          </ul>
        </div>
        <div className="min-w-0">
          <h2 className={eyebrow}>Analyze &amp; act</h2>
          <div data-hub>{hub}</div>
        </div>
        <div>
          <h2 className={eyebrow}>Verified outputs</h2>
          <div data-outputs>{outputs}</div>
        </div>
        <div className="col-span-3 mt-4">{result}</div>
      </div>

      <div className="space-y-5 min-[1180px]:hidden">
        <div>
          <h2 className={eyebrow}>Connected inputs</h2>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SOURCES.map((s) => (
              <li key={s.id} className={s.id === "devices" ? "sm:col-span-2 sm:mx-auto sm:w-[calc(50%-0.25rem)]" : ""}>
                <SourceTile id={s.id} lit={lit.has(s.id)} activity={activity[s.id]} />
              </li>
            ))}
          </ul>
        </div>
        <FlowConnector compact />
        <div className="mx-auto max-w-[25rem]">
          <h2 className={eyebrow}>Analyze &amp; act</h2>
          {hub}
        </div>
        <FlowConnector compact />
        <div className="mx-auto max-w-[25rem]">
          <h2 className={eyebrow}>Verified outputs</h2>
          {outputs}
          <div className="mt-4">{result}</div>
        </div>
      </div>
    </figure>
  );
}

/* ───────────────────────── connectors ───────────────────────── */

/** Shared input rail with one inbound arrow and one outbound arrow. */
function Connectors({ scopeRef }: { scopeRef: React.RefObject<HTMLElement | null> }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const [geom, setGeom] = useState<{ w: number; h: number; branches: string[]; rail: string; inbound: string; outbound: string } | null>(null);
  const measure = useCallback(() => {
    const scope = scopeRef.current;
    const grid = scope?.querySelector<HTMLElement>("[data-desktop-stage]");
    const hub = grid?.querySelector<HTMLElement>("[data-hub]");
    const outputs = grid?.querySelector<HTMLElement>("[data-outputs]");
    if (!grid || !hub || !outputs || getComputedStyle(grid).display === "none") return;
    const box = grid.getBoundingClientRect();
    const rel = (el: Element) => {
      const r = el.getBoundingClientRect();
      return { left: r.left - box.left, right: r.right - box.left, cy: r.top - box.top + r.height / 2 };
    };
    const H = rel(hub);
    const O = rel(outputs);
    const tiles = [...grid.querySelectorAll<HTMLElement>("[data-tile]")].map(rel);
    if (!tiles.length) return;
    const railX = tiles[0]!.right + 10;
    setGeom({
      w: box.width, h: box.height,
      branches: tiles.map(t => `M ${t.right + 2} ${t.cy} H ${railX}`),
      rail: `M ${railX} ${tiles[0]!.cy} V ${tiles[tiles.length - 1]!.cy}`,
      inbound: `M ${railX} ${H.cy} H ${H.left - 4}`,
      outbound: `M ${H.right + 3} ${H.cy} H ${O.left - 4}`,
    });
  }, [scopeRef]);
  useEffect(() => {
    measure();
    const scope = scopeRef.current;
    if (!scope || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(scope);
    return () => observer.disconnect();
  }, [measure, scopeRef]);
  if (!geom) return null;
  return (
    <svg data-flow-connectors aria-hidden="true" width={geom.w} height={geom.h} viewBox={`0 0 ${geom.w} ${geom.h}`} className="pointer-events-none absolute inset-0">
      <defs>
        <marker id={`${uid}-arrow`} viewBox="0 0 6 6" refX="5" refY="3" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M1 1 L5 3 L1 5" fill="none" className="stroke-signal-ink" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </marker>
      </defs>
      <g fill="none" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="stroke-signal-ink/35">
        {geom.branches.map(d => <path key={d} d={d} />)}
        <path data-input-rail d={geom.rail} />
        <path data-inbound d={geom.inbound} markerEnd={`url(#${uid}-arrow)`} />
        <path data-outbound d={geom.outbound} markerEnd={`url(#${uid}-arrow)`} />
      </g>
    </svg>
  );
}

/* ───────────────────────── tiles, hub, result, controls ───────────────────────── */

function FlowConnector({ compact = false }: { compact?: boolean }) {
  return (
    <div aria-hidden="true" className={`relative mx-auto w-4 ${compact ? "h-6" : "h-8"}`}>
      <span className={`absolute left-1/2 top-0 w-px -translate-x-1/2 bg-signal/60 ${compact ? "h-4" : "h-6"}`} />
      <svg viewBox="0 0 12 8" className="absolute bottom-0 left-1/2 size-3 -translate-x-1/2 text-signal/70" fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinecap="round" strokeLinejoin="round"><path d="m2 2 4 4 4-4" /></svg>
    </div>
  );
}

/** A connection tile. The second line is reserved so activity labels never change its size. */
function SourceTile({ id, lit, activity }: { id: SourceId; lit: boolean; activity?: string }) {
  return (
    <div data-input-active={lit} className={`flex h-[4.5rem] w-full items-center gap-2.5 rounded-xl border px-3 transition-colors duration-500 ${lit ? "border-signal/50 bg-signal-soft text-ink" : "border-hairline bg-mist text-slate-strong"}`}>
      <span className={`grid size-[2.125rem] shrink-0 place-items-center rounded-xl transition-colors duration-500 ${lit ? "bg-cta/40 text-signal-ink" : "text-slate/60 ring-1 ring-inset ring-hairline"}`}><SourceIcon id={id} className="size-[18px]" /></span>
      <span className="min-w-0">
        <span className="block font-sans text-[12.5px] font-semibold leading-tight">{source(id).label}</span>
        <span key={activity ?? "idle"} className={`${anim.hubFade} mt-1 block font-sans text-[10.5px] font-medium leading-tight ${lit && activity ? "text-signal-ink" : "text-slate/75"}`}>{activity ?? "Connected"}</span>
      </span>
    </div>
  );
}

const OUTPUTS = [
  { label: "Employee configured", scenario: "employee" },
  { label: "Licenses optimized", scenario: null },
  { label: "Routing restored", scenario: "wolf" },
  { label: "Device provisioned", scenario: "employee" },
  { label: "Compliance verified", scenario: null },
];

function OutputCatalog({ scenario, verified }: { scenario: Scenario; verified: boolean }) {
  return (
    <div>
      <ul className="flex flex-col gap-4" aria-label="Example outcomes">
        {OUTPUTS.map(output => {
          const completed = verified && output.scenario === scenario.id;
          return (
            <li key={output.label} data-output-verified={completed} className={`flex h-[4.5rem] items-center gap-2 rounded-xl border px-3 font-sans text-[12px] font-medium leading-snug transition-colors ${completed ? "border-signal/50 bg-signal-soft text-ink" : "border-hairline bg-mist text-slate-strong"}`}>
              <span aria-hidden="true" className={`grid size-4 shrink-0 place-items-center rounded-full ${completed ? "bg-ink text-paper" : "border border-hairline"}`}>{completed ? <Check className="size-2.5" /> : null}</span>
              <span>{output.label}{completed ? <span className="sr-only"> — verified in this example</span> : null}</span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 font-sans text-[10px] text-slate">Example outcomes</p>
    </div>
  );
}

function ChatHub({ scenario, phase, typed, at, onApprove, approvedByClick, interactive }: { scenario: Scenario; phase: Phase; typed: string; at: (p: Phase) => boolean; onApprove: () => void; approvedByClick: boolean; interactive: boolean }) {
  const isChange = Object.keys(scenario.writes).length > 0;
  const wolf = scenario.agent === "wolf";
  return (
    <div className="flex h-[26.5rem] flex-col overflow-hidden rounded-[1.125rem] bg-paper-raised shadow-card ring-1 ring-hairline">
      <div className="relative isolate flex h-[3.125rem] shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-chat-header px-5 text-paper-raised">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(var(--color-paper-raised) 1px, transparent 1px), linear-gradient(90deg, var(--color-paper-raised) 1px, transparent 1px)", backgroundSize: "20px 20px", maskImage: "linear-gradient(100deg, transparent, black 55%, transparent)" }} />
        <svg viewBox={`${EDGE_BBOX.x} ${EDGE_BBOX.y} ${EDGE_BBOX.w} ${EDGE_BBOX.h}`} role="img" aria-label="Edge" className="h-auto w-28 shrink-0"><EdgePaths gold="fill-signal" navy="fill-paper-raised" /></svg>

      </div>

      {/* Fixed-height body so phases never change the hub's size. */}
      <div className="flex min-h-0 flex-1 flex-col gap-5 px-5 pt-5" aria-live="off">
        <div className="flex min-h-[2.5rem] justify-end">
          <p className={`max-w-[86%] rounded-2xl rounded-br-md bg-mist px-4 py-2.5 font-sans text-[13px] leading-snug text-ink ${typed ? "" : "invisible"}`}>
            {typed || scenario.request}
            {phase === "typing" ? <span aria-hidden="true" className={`${anim.hubCaret} ml-px inline-block h-[1em] w-px translate-y-[2px] bg-ink`} /> : null}
          </p>
        </div>

        <div aria-hidden={!at("thinking")} className={`flex items-start gap-3 transition-opacity duration-300 ${at("thinking") ? "opacity-100" : "opacity-0"}`}>
          <span aria-hidden="true" className="mt-0.5 grid size-7 shrink-0 place-items-center overflow-hidden rounded-lg bg-paper text-ink ring-1 ring-hairline">
            {wolf ? (
              <ModuleIcon name="wolf" className="size-[1.35rem] text-ink" />
            ) : (
              <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h7l-1 8 9-12h-7z" /></svg>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="mb-2 font-sans text-[10px] font-semibold uppercase tracking-[0.08em] text-slate">
              {phase === "gathering" ? "Normalizing inputs" : phase === "thinking" ? "Diagnosing" : phase === "verified" ? "Result verified" : phase === "result" ? "Verifying result" : isChange && at("approval") ? "Applying approved action" : isChange ? "Recommended action" : "Analysis ready"}
            </p>
            {wolf ? <p className="mb-1 font-sans text-[9px] font-semibold uppercase tracking-[0.14em] text-slate">The Wolf</p> : null}
            {phase === "thinking" ? (
              <span className={`${anim.hubThinking} inline-flex h-[1.4em] items-center gap-1 pt-1`} aria-label="Thinking">
                <span className="size-1.5 rounded-full bg-slate" /><span className="size-1.5 rounded-full bg-slate" /><span className="size-1.5 rounded-full bg-slate" />
              </span>
            ) : phase === "gathering" ? (
              <p className={`${anim.hubFade} font-sans text-[13px] leading-relaxed text-slate-strong`}>{scenario.finding}</p>
            ) : at("response") ? (
              <p className={`${anim.hubFade} font-sans text-[13px] leading-relaxed text-ink`}>{scenario.response}</p>
            ) : null}

            <div className="mt-4 flex min-h-[2rem] flex-wrap items-center gap-2">
              {isChange && phase === "response" ? (
                <>
                  <Pill tone="wait">Awaiting approval</Pill>
                  <button type="button" onClick={onApprove} disabled={!interactive}
                    className={`${anim.hubFade} inline-flex items-center gap-1.5 rounded-pill bg-ink px-3 py-1.5 font-sans text-[10.5px] font-semibold text-paper-raised transition-colors hover:bg-ink-2 disabled:opacity-70`}>
                    <Check className="size-2.5 text-signal" />Approve example
                  </button>
                </>
              ) : null}
              {isChange && at("approval") ? <Pill tone="done">{approvedByClick ? "Approved by you · demo" : "Approved · simulated"}</Pill> : null}
              {isChange && phase === "executing" ? <Pill tone="wait">{scenario.running}</Pill> : null}
              {at("result") ? <Pill tone={phase === "verified" ? "gold" : "done"}>{phase === "verified" ? "Verified" : "Completed"}</Pill> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 px-5 pb-5 pt-3">
        <div className="flex h-11 items-center gap-2 rounded-pill bg-mist pl-4 pr-1.5 ring-1 ring-hairline">
          <span className="flex-1 font-sans text-[12.5px] text-slate">Message Edge…</span>
          <span aria-hidden="true" className="grid size-8 shrink-0 place-items-center rounded-full bg-cta text-cta-ink">
            <svg viewBox="0 0 16 16" className="size-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M8 13V3M4 7l4-4 4 4" /></svg>
          </span>
        </div>
      </div>
    </div>
  );
}

function Pill({ tone, children }: { tone: "wait" | "done" | "gold"; children: React.ReactNode }) {
  return (
    <span className={`${anim.hubFade} inline-flex items-center gap-1.5 rounded-pill bg-mist px-2.5 py-1 font-sans text-[10px] font-semibold text-slate-strong ring-1 ring-inset ring-hairline`}>
      {tone === "wait" ? <span aria-hidden="true" className={`${anim.stageNode} size-1.5 rounded-full bg-signal`} /> : <span aria-hidden="true" className="grid size-3 place-items-center rounded-full bg-ink text-paper-raised"><Check className="size-2" /></span>}
      {children}
    </span>
  );
}

/** One result card for the active scenario, shaped to the request. Fixed height so it slides in without moving the page. */
function ResultSlot({ scenario, phase, at }: { scenario: Scenario; phase: Phase; at: (p: Phase) => boolean }) {
  const show = at("gathering");
  const complete = at("result");
  const verified = phase === "verified";
  const r = scenario.result;
  const isChange = Object.keys(scenario.writes).length > 0;
  return (
    <div className="relative h-64 min-[1180px]:h-[8.5rem]">
      <div
        key={scenario.id}
        aria-hidden={!show}
        className={`absolute inset-0 overflow-hidden rounded-2xl border border-hairline bg-paper-raised shadow-card transition-all duration-500 ease-out-soft ${show ? `visible ${anim.hubSlide} translate-y-0 opacity-100` : "invisible translate-y-2 opacity-0"}`}
      >
        <div className="grid h-full grid-rows-[auto_1fr] min-[1180px]:grid-cols-[12rem_minmax(0,1fr)] min-[1180px]:grid-rows-1">
          <div className="flex items-center gap-3 border-b border-hairline bg-mist px-4 py-3 min-[1180px]:border-b-0 min-[1180px]:border-r">
            <span aria-hidden="true" className={`grid size-9 shrink-0 place-items-center rounded-xl transition-colors duration-500 ${verified ? "bg-ink text-cta" : "bg-signal-soft text-signal-ink"}`}>
              {verified ? <Check className={`${anim.hubPop} size-4`} /> : <span className={`${anim.stageNode} size-2 rounded-full bg-cta`} />}
            </span>
            <div className="min-w-0">
              <p className="font-display text-sm font-semibold leading-snug text-ink">
                {verified ? (isChange ? "Verified resolution" : "Verified outcome") : complete ? (isChange ? "Resolution complete" : "Outcome ready") : isChange && phase === "response" ? "Awaiting approval" : phase === "approval" ? "Resolution approved" : phase === "executing" ? "Applying resolution" : isChange ? "Resolution in progress" : "Analysis in progress"}
              </p>
              <p className="mt-1 font-sans text-[10px] leading-relaxed text-slate">{complete ? (isChange ? "Approved · Completed" : "Read-only · Completed") : phase === "approval" || phase === "executing" ? "Approved · In progress" : isChange ? "No changes completed yet" : "Read-only · In progress"}</p>
            </div>
          </div>
          <div className="flex min-w-0 flex-col justify-center px-4 py-3">
            <p className="font-display text-sm font-semibold leading-snug text-ink">{complete ? r.title : scenario.label}</p>
            {!complete ? (
              <p className="mt-2 border-l-2 border-cta pl-3 font-sans text-xs leading-relaxed text-slate-strong">{phase === "executing" ? scenario.running : isChange && phase === "response" ? "Waiting for approval before applying the recommended change." : phase === "approval" ? "Approval received. Preparing to apply the recommended change." : scenario.finding}</p>
            ) : r.kind === "checklist" ? (
              <ul className="mt-2 grid grid-cols-2 gap-2">
                {r.items.map((it, i) => (
                  <li key={it} className="flex min-w-0 items-start gap-1.5 rounded-md bg-mist px-2 py-1.5 font-sans text-[11px] leading-snug text-slate-strong">
                    <span aria-hidden="true" className="mt-0.5 grid size-3 shrink-0 place-items-center rounded-full bg-ink text-paper-raised" style={{ transitionDelay: `${i * 120}ms` }}><Check className="size-2" /></span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            ) : r.kind === "findings" ? (
              <ul className="mt-2 space-y-1.5">
                {r.items.map((it) => <li key={it} className="flex items-start gap-2 font-sans text-xs leading-snug text-slate-strong"><span aria-hidden="true" className="mt-1.5 size-1 shrink-0 bg-cta" /><span>{it}</span></li>)}
              </ul>
            ) : r.kind === "dashboard" ? (
              <ul className="mt-2 grid grid-cols-3 gap-2" aria-label="Dashboard preview, illustrative">
                {r.charts.map((c) => (
                  <li key={c.label} className="min-w-0 rounded-lg border border-hairline/70 bg-mist px-2 py-2">
                    <p className="min-h-[2.4em] font-sans text-[10px] font-medium leading-tight text-slate">{c.label}</p>
                    <div className="mt-1 flex h-5 items-end gap-0.5" aria-hidden="true">
                      {c.bars.map((v, i) => <span key={i} className={`flex-1 rounded-t-[2px] ${i === c.bars.length - 1 ? "bg-cta" : "bg-ink/35"}`} style={{ height: `${v * 100}%` }} />)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 border-l-2 border-cta pl-3 font-sans text-xs leading-relaxed text-slate-strong">{r.body}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Check({ className }: { className: string }) {
  return <svg viewBox="0 0 12 12" className={className} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.5 5 9l4.5-6" /></svg>;
}

/** Category line icons, 24-unit grid, currentColor stroke. Categories, never vendors. */
const ICONS: Record<SourceId, React.ReactNode> = {
  ucaas: <><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h3l1.5 4-2 1.3a8.5 8.5 0 0 0 4.7 4.7L14 12l4 1.5v3a1.5 1.5 0 0 1-1.5 1.5A12.5 12.5 0 0 1 4 5.5z" /><path d="M15 5.5a3.5 3.5 0 0 1 3.5 3.5M15 2a7 7 0 0 1 7 7" /></>,
  "contact-center": <><path d="M4 13v-2a8 8 0 0 1 16 0v2" /><rect x="3" y="12" width="4" height="6" rx="1.5" /><rect x="17" y="12" width="4" height="6" rx="1.5" /><path d="M19 18v1.5a2 2 0 0 1-2 2h-4" /></>,
  directories: <><ellipse cx="12" cy="5.5" rx="7" ry="2.5" /><path d="M5 5.5v13c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-13" /><path d="M5 12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5" /></>,
  workflows: <><rect x="3" y="3.5" width="6" height="5" rx="1.2" /><rect x="15" y="3.5" width="6" height="5" rx="1.2" /><rect x="9" y="15.5" width="6" height="5" rx="1.2" /><path d="M6 8.5V12h12V8.5M12 12v3.5" /></>,
  devices: <><rect x="3" y="5" width="12" height="9" rx="1.5" /><path d="M7 18h4M9 14v4" /><rect x="17" y="8" width="4" height="10" rx="1" /></>,
};

function SourceIcon({ id, className = "size-5" }: { id: SourceId; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      {ICONS[id]}
    </svg>
  );
}
